import { Button, Table, Tag, Badge, Popover, Icon } from "antd";
import moment from "moment";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import styled from 'styled-components';

import { Container, Main, SummaryContainer } from "./styles";
import { getFormattedEvent } from "~/containers/Documents/helpers";
import { EmailLogsModal } from "~/containers/Documents/Modals/EmailLogsModal";
import { EmailBodyPreviewModal } from "./components/EmailBodyPreviewModal";
import EmailsLogsSkeleton from "./components/EmailsLogsSkeleton";
import SummarySkeleton from "./components/SummarySkeleton";
import SummaryCard from "./components/SummaryCard";
import { BlacklistDrawer } from "./components/BlacklistDrawer";
import { ModuleHeader } from '~/components/ModuleHeader';
import { STORAGE } from "~/constants/storage.constants";
import { Subtitle } from '~/components/ModuleHeader/styles';
import { normalizeToFilter } from '~/containers/KnowledgeBase/utils';
import { getDefaultPaginationConfig } from '~/utils/get-default-pagination-config';
import Search from '~/_shared/components/Search';
import FilterPillsBar from '~/_shared/components/FilterPillsBar';
import RestoredFiltersNotice from '~/_shared/components/RestoredFiltersNotice';
import usePersistedFilters from '~/_shared/hooks/usePersistedFilters';
import {
  EMAILS_LOGS_FILTERS_KEY,
  EMAILS_LOGS_PATH,
  getDefaultEmailsLogsFilters,
  isFailedRoute,
  getEffectiveStatus,
  getStatusSelection,
  countActiveEmailsLogsFilters,
} from './helpers';

// Constants
const EMAIL_STATUS_FILTERS = [
  { text: 'Rascunho', value: 'draft' },
  { text: 'Enviado', value: 'delivered' },
  { text: 'Aberto', value: 'opened' },
  { text: 'Clicado', value: 'clicked' },
  { text: 'Marcado como spam', value: 'complained' },
  { text: 'Falhou', value: 'failed' },
  { text: 'Inscrição cancelada', value: 'unsubscribed' },
  { text: 'Envio solicitado', value: 'pendent' },
  { text: 'Blacklist', value: 'blacklist' },
  { text: 'Duplicado', value: 'duplicated' },
];

const FAILED_EVENTS = ['failed', 'complained', 'unsubscribed', 'blacklist', 'duplicated'];

const STATUS_LABELS = { draft: 'Rascunho', pendent: 'Envio solicitado', delivered: 'Enviado', opened: 'Aberto', clicked: 'Clicado', failed: 'Falhou' };
const STATUS_COLORS = { draft: '#8c8c8c', pendent: '#1890ff', delivered: '#52c41a', opened: '#722ed1', clicked: '#faad14', failed: '#f5222d' };

// Utility functions
const formatDateTime = (datetime) => moment(datetime).format("DD/MM/YYYY HH:mm:ss");

const getCustomerName = (log, customersById) => 
  (customersById[log.customer_id] && customersById[log.customer_id].name) || log.customer_name;

const filterEmails = (logs, filter) => logs.filter((log) => {
  const customerNameMatches = normalizeToFilter(log.customer_name).includes(filter);
  const emailMatches = normalizeToFilter(log.email_to).includes(filter);
  return customerNameMatches || emailMatches;
});

const BlacklistButton = styled(Button)`
  &&& {
    color: #fff;
    background: #000;
    border-color: #000;
    
    &:hover {
      color: #fff;
      background: #262626;
      border-color: #262626;
    }

    &:active {
      color: #fff;
      background: #000;
      border-color: #000;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
`;

export const EmailsLogs = ({ refresh, setSelectedDocument }) => {
  // State Management
  const { pathname } = useLocation();
  const history = useHistory();
  const { emailsLogs, isLoading, customersById } = useSelector((state) => ({
    emailsLogs: state.emailsLogs.list,
    isLoading: state.emailsLogs.isLoading,
    customersById: state.customersById,
  }));

  // Filters kept across refreshes for a few minutes (per tenant/user/page).
  // The list is loaded by AppContext (last 7 days) and filtered client-side.
  const persisted = usePersistedFilters(EMAILS_LOGS_FILTERS_KEY, getDefaultEmailsLogsFilters);
  const applied = persisted.filters;
  const setApplied = persisted.setFilters;
  const filter = normalizeToFilter(applied.search);
  const isFailedPath = isFailedRoute(pathname);
  // The URL wins: on /erros the status is always "failed".
  const selectedStatus = getEffectiveStatus(applied, pathname);

  // Opening /erros drops a status saved on /emails, so it can't come back later.
  useEffect(() => {
    if (isFailedPath && applied.status) {
      setApplied({ status: null });
    }
  }, [isFailedPath, applied.status, setApplied]);
  
  // Modal States
  const [emailsLogsModal, setEmailsLogsModal] = useState({
    isOpen: false,
    logs: [],
  });
  const [emailBodyPreviewModal, setEmailBodyPreviewModal] = useState({
    isOpen: false,
    data: null,
    initialLogId: [],
  });
  const [isBlacklistDrawerOpen, setIsBlacklistDrawerOpen] = useState(false);

  // Modal Handlers
  const openEmailsLogsModal = useCallback((logs) => {
    setEmailsLogsModal({
      isOpen: true,
      logs,
    });
  }, []);

  const closeEmailsLogsModal = useCallback(() => {
    setEmailsLogsModal({
      isOpen: false,
      logs: [],
    });
  }, []);

  const openEmailBodyPreviewModal = useCallback(({ initialLogId, data }) => {
    setEmailBodyPreviewModal({
      isOpen: true,
      data,
      initialLogId,
    });
  }, []);

  const closeEmailBodyPreviewModal = useCallback(() => {
    setEmailBodyPreviewModal({
      isOpen: false,
      data: null,
      initialLogId: null,
    });
  }, []);

  // Filtering and Data Processing
  // Uses react-router's history (not window.history) so `pathname` follows.
  const goTo = useCallback((path) => {
    if (pathname !== path) history.push(path);
  }, [history, pathname]);

  const handleCardClick = useCallback((status) => {
    const selection = getStatusSelection(selectedStatus, status);
    setApplied({ status: selection.status });
    goTo(selection.path);
  }, [selectedStatus, setApplied, goTo]);

  const handleClearAll = useCallback(() => {
    persisted.resetFilters();
    goTo(EMAILS_LOGS_PATH);
  }, [persisted.resetFilters, goTo]);

  const handleRemoveStatus = useCallback(() => {
    setApplied({ status: null });
    goTo(EMAILS_LOGS_PATH);
  }, [setApplied, goTo]);

  // Count Calculations
  const getStatusCount = useCallback((status) => {
    return (emailsLogs || []).filter(log => log.event === status).length;
  }, [emailsLogs]);

  const getFailedCount = useCallback(() => {
    return (emailsLogs || []).filter(log => 
      log.event === 'failed' || 
      log.event === 'blacklist' || 
      log.event === 'unsubscribed' || 
      log.event === 'complained'
    ).length;
  }, [emailsLogs]);

  const getFailedCounts = useCallback(() => {
    return {
      failed: (emailsLogs || []).filter(log => log.event === 'failed').length,
      blacklist: (emailsLogs || []).filter(log => log.event === 'blacklist').length,
      unsubscribed: (emailsLogs || []).filter(log => log.event === 'unsubscribed').length,
      complained: (emailsLogs || []).filter(log => log.event === 'complained').length,
      duplicated: (emailsLogs || []).filter(log => log.event === 'duplicated').length
    };
  }, [emailsLogs]);

  const totalEmails = useMemo(() => (emailsLogs || []).length, [emailsLogs]);

  // Data Processing
  const filteredEmailsLogs = useMemo(() => {
    return (emailsLogs || [])
      .map((log) => ({
        ...log,
        customer_name: getCustomerName(log, customersById),
      }))
      .filter((log) => {
        const matchesSearch = filterEmails([log], filter).length > 0;
        const matchesStatusFilter = isFailedPath
          ? FAILED_EVENTS.includes(log.event)
          : !selectedStatus || log.event === selectedStatus;
        return matchesSearch && matchesStatusFilter;
      });
  }, [emailsLogs, customersById, filter, selectedStatus, isFailedPath]);

  const formattedEmailsLogs = useMemo(() => {
    return filteredEmailsLogs.map((log) => ({
      ...log,
      formatted_sent_at: formatDateTime(log.datetime),
      email_status: getFormattedEvent(log.event),
    }));
  }, [filteredEmailsLogs]);

  const blacklistEmails = useMemo(() => {
    const uniqueEmails = new Set();
    return (emailsLogs || [])
      .filter(log => {
        if (log.event === 'blacklist' && !uniqueEmails.has(log.email_to)) {
          uniqueEmails.add(log.email_to);
          return true;
        }
        return false;
      });
  }, [emailsLogs]);

  // Event Handlers
  const handleStatusClick = useCallback((related_logs, customer_file_id) => {
    if (customer_file_id) {
      setSelectedDocument(customer_file_id);
    } else {
      openEmailsLogsModal(related_logs);
    }
  }, [setSelectedDocument, openEmailsLogsModal]);

  const handleOpenBlacklistDrawer = useCallback((e) => {
    e.preventDefault();
    console.log('Opening drawer, current state:', isBlacklistDrawerOpen);
    setIsBlacklistDrawerOpen(true);
  }, [isBlacklistDrawerOpen]);

  const handleCloseBlacklistDrawer = useCallback(() => {
    console.log('Closing drawer');
    setIsBlacklistDrawerOpen(false);
  }, []);

  // Table Configuration
  const columns = useMemo(() => [
    {
      title: "Título",
      key: "title",
      dataIndex: "subject",
      ellipsis: true,
      width: 400,
      fixed: 'left',
      render: (text) => (
        <div style={{ 
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '350px'
        }}>
          {text}
        </div>
      ),
    },
    {
      title: "Cliente",
      key: "customer",
      dataIndex: "customer_name",
      ellipsis: true,
      width: 300,
    },
    {
      title: "E-mail",
      key: "email",
      dataIndex: "email_to",
      ellipsis: true,
      width: 300,
    },
    {
      title: "Data de envio",
      key: "formatted_sent_at",
      defaultSortOrder: "ascend",
      sorter: (a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''),
      width: 200,
      dataIndex: "formatted_sent_at",
      ellipsis: true,
    },
    {
      title: "Status do e-mail",
      key: "email_status",
      dataIndex: "email_status",
      filters: EMAIL_STATUS_FILTERS,
      onFilter: (value, record) => record.event === value,
      width: 150,
      ellipsis: true,
      render: (status, { related_logs, customer_file_id }) => {
        if (!status) return null;
        return (
          <Tag
            style={{ cursor: "pointer" }}
            onClick={() => handleStatusClick(related_logs, customer_file_id)}
            color={status.color}
          >
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: "Prévia",
      key: "id",
      align: "center",
      width: 80,
      dataIndex: "id",
      ellipsis: true,
      fixed: 'right',
      render: (_, record) => (
        <Button
          icon="eye"
          onClick={() => openEmailBodyPreviewModal({ initialLogId: record.id, data: record })}
        />
      ),
    },
  ], [handleStatusClick, openEmailBodyPreviewModal]);

  const scrollX = useMemo(() => columns.reduce((total, col) => total + (col.width || 0), 0), [columns]);

  // Pills reflect the applied filters (status from the URL on /erros).
  const pills = [];
  if (selectedStatus) {
    pills.push({
      key: 'status',
      label: STATUS_LABELS[selectedStatus] || selectedStatus,
      color: STATUS_COLORS[selectedStatus] || '#1890ff',
      onRemove: handleRemoveStatus,
    });
  }
  if (applied.search) {
    pills.push({
      key: 'search',
      label: 'Busca: ' + applied.search,
      color: '#722ed1',
      icon: 'search',
      onRemove: () => setApplied({ search: '' }),
    });
  }
  const activeFilterCount = countActiveEmailsLogsFilters(applied, pathname);

  // Only what came from storage: on /erros the status comes from the URL.
  const restoredSummary = pills
    .filter((pill) => !(isFailedPath && pill.key === 'status'))
    .map((pill) => pill.label)
    .join(' · ');

  return (
    <Container>
      <ModuleHeader
          breadcrumbs={['Itens Enviados', 'E-mails Enviados']}
          title={
            <span>
              E-mails enviados <Subtitle>| nos ultimos 7 dias</Subtitle>
            </span>
          }
          search={
            <Search
              placeholder="Pesquisar pelo nome do cliente ou e-mail"
              value={applied.search}
              onChange={({ target }) => setApplied({ search: target.value })}
            />
          }
          center={
            <>
              <Popover
                trigger="click"
                placement="bottomRight"
                content={
                  <div style={{ width: 320 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#262626' }}>Filtros</span>
                      {activeFilterCount > 0 && (
                        <a style={{ fontSize: 12, color: '#f5222d' }} onClick={handleClearAll}>
                          Limpar tudo
                        </a>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[
                          { value: 'draft', label: 'Rascunho', color: '#8c8c8c', count: getStatusCount('draft') },
                          { value: 'pendent', label: 'Envio solicitado', color: '#1890ff', count: getStatusCount('pendent') },
                          { value: 'delivered', label: 'Enviado', color: '#52c41a', count: getStatusCount('delivered') },
                          { value: 'opened', label: 'Aberto', color: '#722ed1', count: getStatusCount('opened') },
                          { value: 'clicked', label: 'Clicado', color: '#faad14', count: getStatusCount('clicked') },
                          { value: 'failed', label: 'Falhou', color: '#f5222d', count: getFailedCount() },
                        ].map(function(opt) {
                          var isActive = selectedStatus === opt.value;
                          return (
                            <div
                              key={opt.value}
                              onClick={function() { handleCardClick(opt.value); }}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                                border: '1.5px solid ' + (isActive ? opt.color : '#e8e8e8'),
                                background: isActive ? opt.color + '0a' : '#fafafa',
                                transition: 'all 0.2s',
                              }}
                            >
                              <span style={{ fontSize: 12, color: isActive ? '#262626' : '#8c8c8c' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, display: 'inline-block', marginRight: 8 }} />
                                {opt.label}
                              </span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: isActive ? opt.color : '#bfbfbf' }}>{opt.count}</span>
                                {isActive && <Icon type="check" style={{ color: opt.color, fontSize: 12 }} />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                }
              >
                <Badge
                  count={activeFilterCount}
                  offset={[-4, 0]}
                  style={{ backgroundColor: '#722ed1' }}
                >
                  <Button icon="bars" style={{ borderRadius: 8 }}>Filtros</Button>
                </Badge>
              </Popover>
              {blacklistEmails.length > 0 && (
                <Badge count={blacklistEmails.length} offset={[-5, 5]}>
                  <BlacklistButton
                    onClick={handleOpenBlacklistDrawer}
                    type="button"
                  >
                    Blacklist
                  </BlacklistButton>
                </Badge>
              )}
            </>
          }
          advanced={
            pills.length > 0 ? (
              React.createElement(FilterPillsBar, {
                pills: pills,
                onClearAll: handleClearAll,
              })
            ) : null
          }
      />
      <RestoredFiltersNotice
        visible={persisted.wasRestored && restoredSummary !== ''}
        summary={restoredSummary}
        onReset={handleClearAll}
        onDismiss={persisted.dismissRestored}
      />
      <Main>
        {isLoading ? (
          <>
            <SummarySkeleton />
            <EmailsLogsSkeleton />
          </>
        ) : (
          <>
            <SummaryContainer>
              <SummaryCard
                status="draft"
                count={getStatusCount('draft')}
                total={totalEmails}
                color="#8c8c8c"
                isActive={selectedStatus === 'draft'}
                onClick={() => handleCardClick('draft')}
              />
              <SummaryCard
                status="pendent"
                count={getStatusCount('pendent')}
                total={totalEmails}
                color="#1890ff"
                isActive={selectedStatus === 'pendent'}
                onClick={() => handleCardClick('pendent')}
              />
              <SummaryCard
                status="delivered"
                count={getStatusCount('delivered')}
                total={totalEmails}
                color="#52c41a"
                isActive={selectedStatus === 'delivered'}
                onClick={() => handleCardClick('delivered')}
              />
              <SummaryCard
                status="opened"
                count={getStatusCount('opened')}
                total={totalEmails}
                color="#722ed1"
                isActive={selectedStatus === 'opened'}
                onClick={() => handleCardClick('opened')}
              />
              <SummaryCard
                status="clicked"
                count={getStatusCount('clicked')}
                total={totalEmails}
                color="#faad14"
                isActive={selectedStatus === 'clicked'}
                onClick={() => handleCardClick('clicked')}
              />
              <SummaryCard
                status="failed"
                count={getFailedCount()}
                total={totalEmails}
                color="#f5222d"
                isActive={selectedStatus === 'failed'}
                onClick={() => handleCardClick('failed')}
                failedCounts={getFailedCounts()}
              />
            </SummaryContainer>
            <Table
              rowKey="id"
              bordered
              dataSource={formattedEmailsLogs}
              scroll={{ y: "calc(100vh - 400px)", x: scrollX }}
              pagination={getDefaultPaginationConfig({
                storageKey: STORAGE.defaultPageSize.emailsLogs
              })}
              columns={columns}
            />
          </>
        )}
      </Main>
      <EmailLogsModal
        isOpen={emailsLogsModal.isOpen}
        closeModal={closeEmailsLogsModal}
        logs={emailsLogsModal.logs}
      />
      <EmailBodyPreviewModal
        isOpen={emailBodyPreviewModal.isOpen}
        onClose={closeEmailBodyPreviewModal}
        initialLogId={emailBodyPreviewModal.initialLogId}
        data={emailBodyPreviewModal.data}
      />
      {isBlacklistDrawerOpen && (
        <BlacklistDrawer
          isOpen={isBlacklistDrawerOpen}
          onClose={handleCloseBlacklistDrawer}
          blacklistEmails={blacklistEmails}
        />
      )}
    </Container>
  );
};
